import { IconChevronDown, IconSearch, IconX } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import {
  selectRegions,
  selectRegionsLoading,
  selectTenant,
  useAppSelector,
} from '@/store'
import { HierarchyNode, Region } from '@/types'

import {
  BranchType,
  createPredefinedHierarchySelection,
  emptyHierarchySelection,
  getAllLeafNodes,
  getLeafNodeIds,
  HierarchySelection,
  isLeafNode,
  regionsToHierarchy,
  searchLeafNodes,
  Selection,
} from './branchSelectUtil'
import LevelColumn from './LevelColumn'
import TreeNode from './TreeNode'

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

export interface BranchSelectProps {
  onChange?: (selection: Selection) => void
  value?: string
  branchType?: BranchType
  resetBranchSelect?: React.MutableRefObject<boolean>
  /** When provided, use these regions instead of the global Redux regions.
   *  Used by PnL page to show country-specific regions (e.g. UK regions for GBP). */
  regionsOverride?: Region[]
}

// ----------------------------------------------------------------------
// MAIN COMPONENT
// ----------------------------------------------------------------------

export const BranchSelect = ({
  onChange,
  value,
  branchType,
  resetBranchSelect,
  regionsOverride,
}: BranchSelectProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const tenant = useAppSelector(selectTenant)
  const globalRegions = useAppSelector(selectRegions)
  const globalRegionsLoading = useAppSelector(selectRegionsLoading)

  // Use override regions when provided, otherwise fall back to global Redux regions
  const REGIONS = regionsOverride ?? globalRegions
  const regionsLoading = regionsOverride ? false : globalRegionsLoading

  // Convert regions to hierarchy nodes
  const hierarchyData = useMemo(() => {
    if (REGIONS.length === 0) return []
    if (id === 'spend' || id === 'profit-and-loss')
      return regionsToHierarchy(REGIONS)
    return regionsToHierarchy(
      REGIONS.filter((region) => region.name !== 'Other')
    )
  }, [id, REGIONS])

  // State
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selection, setSelection] = useState<HierarchySelection>(
    emptyHierarchySelection
  )
  const [expandedPath, setExpandedPath] = useState<string[]>([])
  const hasInitialized = useRef(false)

  // Track previous props to detect external changes
  const prevValueRef = useRef(value)
  const prevBranchTypeRef = useRef(branchType)
  const prevRegionsRef = useRef(REGIONS)

  // When the regions source changes (e.g. switching from US to UK regions via override),
  // force reinitialization so the component picks up the new region data
  useEffect(() => {
    if (prevRegionsRef.current !== REGIONS && REGIONS.length > 0) {
      prevRegionsRef.current = REGIONS
      hasInitialized.current = false
      setSelection(emptyHierarchySelection)
      setExpandedPath([])
      setSearchQuery('')
    }
  }, [REGIONS])

  // Get all leaf nodes for "All Stores" calculation
  const allLeafNodes = useMemo(
    () => getAllLeafNodes(hierarchyData),
    [hierarchyData]
  )

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    return searchLeafNodes(hierarchyData, searchQuery)
  }, [hierarchyData, searchQuery])

  // Get columns to display based on expanded path
  const columnsToRender = useMemo(() => {
    const columns: {
      title: string
      nodes: HierarchyNode[]
      levelIndex: number
    }[] = []

    // First column is always root level
    if (hierarchyData.length > 0) {
      const firstLevel = hierarchyData[0]?.level || 'Region'
      columns.push({
        title: firstLevel.charAt(0).toUpperCase() + firstLevel.slice(1),
        nodes: hierarchyData,
        levelIndex: 0,
      })
    }

    // Add columns for each expanded node
    let currentNodes = hierarchyData
    for (let i = 0; i < expandedPath.length; i++) {
      const expandedId = expandedPath[i]
      const expandedNode = currentNodes.find((n) => n.id === expandedId)

      if (expandedNode?.children && expandedNode.children.length > 0) {
        const childLevel = expandedNode.children[0]?.level || `Level ${i + 2}`
        columns.push({
          title: childLevel.charAt(0).toUpperCase() + childLevel.slice(1),
          nodes: expandedNode.children,
          levelIndex: i + 1,
        })
        currentNodes = expandedNode.children
      }
    }

    return columns
  }, [hierarchyData, expandedPath])

  // Convert HierarchySelection to Selection for onChange callback
  const convertToSelection = useCallback(
    (hierSel: HierarchySelection): Selection => {
      // Extract region IDs (numeric) from selected IDs
      const selectedRegionIds = Array.from(hierSel.selectedIds)
        .filter((id): id is string => typeof id === 'string')
        .filter((id) => id.startsWith('region-'))
        .map((id) => parseInt(id.replace('region-', ''), 10))

      return {
        selectedRegions: selectedRegionIds,
        selectedBranches: Array.from(hierSel.selectedLeafIds).filter(
          (id): id is string => typeof id === 'string'
        ),
        selectedBranchNames: hierSel.selectedLeafNames,
        branchType: hierSel.branchType,
        predefinedSelection: hierSel.predefinedSelection,
      }
    },
    []
  )

  // Initialize selection from value prop
  useEffect(() => {
    if (hierarchyData.length === 0) return
    if (hasInitialized.current) return

    let initialSelection = emptyHierarchySelection

    if (branchType === 'all') {
      initialSelection = createPredefinedHierarchySelection(
        'all',
        hierarchyData,
        tenant
      )
    } else if (branchType === 'same') {
      initialSelection = createPredefinedHierarchySelection(
        'same',
        hierarchyData,
        tenant
      )
    } else if (branchType === 'new') {
      initialSelection = createPredefinedHierarchySelection(
        'new',
        hierarchyData,
        tenant
      )
    } else if (!value && !branchType) {
      // Default selection based on tenant
      const defaultType = tenant === 'blankstreet' ? 'all' : 'same'
      initialSelection = createPredefinedHierarchySelection(
        defaultType,
        hierarchyData,
        tenant
      )
    } else if (value) {
      // Parse value and find matching leaf nodes
      const branchNames = value.split(',').map((name) => name.trim())
      const matchedLeaves = allLeafNodes.filter((leaf) =>
        branchNames.includes(leaf.name)
      )

      const selectedLeafIds = new Set(matchedLeaves.map((l) => l.id))
      initialSelection = {
        selectedIds: selectedLeafIds,
        selectedLeafIds,
        selectedLeafNames: matchedLeaves.map((l) => l.name),
        branchType: 'regular',
        predefinedSelection: null,
      }
    }

    setSelection(initialSelection)
    hasInitialized.current = true

    if (initialSelection.selectedLeafIds.size > 0) {
      onChange?.(convertToSelection(initialSelection))
    }
  }, [
    hierarchyData,
    value,
    branchType,
    tenant,
    allLeafNodes,
    onChange,
    convertToSelection,
  ])

  // Helper function to derive selection from props
  const deriveSelectionFromProps = useCallback(
    (
      propValue: string | undefined,
      propBranchType: BranchType | undefined
    ): HierarchySelection => {
      if (hierarchyData.length === 0) return emptyHierarchySelection

      if (propBranchType === 'all') {
        return createPredefinedHierarchySelection('all', hierarchyData, tenant)
      } else if (propBranchType === 'same') {
        return createPredefinedHierarchySelection('same', hierarchyData, tenant)
      } else if (propBranchType === 'new') {
        return createPredefinedHierarchySelection('new', hierarchyData, tenant)
      } else if (propValue) {
        // Parse value and find matching leaf nodes
        const branchNames = propValue.split(',').map((name) => name.trim())
        const matchedLeaves = allLeafNodes.filter((leaf) =>
          branchNames.includes(leaf.name)
        )

        if (matchedLeaves.length > 0) {
          const selectedLeafIds = new Set(
            matchedLeaves.map((l) => String(l.id))
          )
          return {
            selectedIds: selectedLeafIds,
            selectedLeafIds,
            selectedLeafNames: matchedLeaves.map((l) => l.name),
            branchType: 'regular',
            predefinedSelection: null,
          }
        }
      }

      // Default selection based on tenant
      if (!propValue && !propBranchType) {
        const defaultType = tenant === 'blankstreet' ? 'all' : 'same'
        return createPredefinedHierarchySelection(
          defaultType,
          hierarchyData,
          tenant
        )
      }

      return emptyHierarchySelection
    },
    [hierarchyData, tenant, allLeafNodes]
  )

  // Sync internal state when props change externally
  useEffect(() => {
    if (hierarchyData.length === 0) return
    if (!hasInitialized.current) return // Wait for initialization first

    const valueChanged = value !== prevValueRef.current
    const branchTypeChanged = branchType !== prevBranchTypeRef.current

    if (valueChanged || branchTypeChanged) {
      prevValueRef.current = value
      prevBranchTypeRef.current = branchType

      const newSelection = deriveSelectionFromProps(value, branchType)
      setSelection(newSelection)
    }
  }, [value, branchType, hierarchyData, deriveSelectionFromProps])

  // Reset handler
  useEffect(() => {
    if (resetBranchSelect?.current && hierarchyData.length > 0) {
      // Recalculate selection from current props instead of emptying
      const resetSelection = deriveSelectionFromProps(value, branchType)
      setSelection(resetSelection)
      setExpandedPath([])
      setSearchQuery('')
      resetBranchSelect.current = false
      onChange?.(convertToSelection(resetSelection))
    }
  }, [
    resetBranchSelect,
    hierarchyData,
    onChange,
    convertToSelection,
    value,
    branchType,
    deriveSelectionFromProps,
  ])

  // Handle node selection
  const handleNodeSelect = useCallback(
    (node: HierarchyNode, checked: boolean) => {
      setSelection((prev) => {
        // Ensure all IDs are strings when copying Sets
        const newSelectedIds = new Set<string>(
          Array.from(prev.selectedIds).filter(
            (id): id is string => typeof id === 'string'
          )
        )
        const newSelectedLeafIds = new Set<string>(
          Array.from(prev.selectedLeafIds).filter(
            (id): id is string => typeof id === 'string'
          )
        )
        const newSelectedLeafNames = [...prev.selectedLeafNames]

        // Get leaf IDs for selection tracking
        getLeafNodeIds(node)

        if (checked) {
          // Add node and all its descendants
          const nodeId = String(node.id)
          newSelectedIds.add(nodeId)
          if (node.children) {
            const addDescendants = (n: HierarchyNode) => {
              const nId = String(n.id)
              newSelectedIds.add(nId)
              if (isLeafNode(n)) {
                newSelectedLeafIds.add(nId)
                if (!newSelectedLeafNames.includes(n.name)) {
                  newSelectedLeafNames.push(n.name)
                }
              }
              n.children?.forEach(addDescendants)
            }
            addDescendants(node)
          } else {
            // Leaf node
            newSelectedLeafIds.add(nodeId)
            if (!newSelectedLeafNames.includes(node.name)) {
              newSelectedLeafNames.push(node.name)
            }
          }
        } else {
          // Remove node and all its descendants
          const removeDescendants = (n: HierarchyNode) => {
            const nId = String(n.id)
            newSelectedIds.delete(nId)
            if (isLeafNode(n)) {
              newSelectedLeafIds.delete(nId)
              const idx = newSelectedLeafNames.indexOf(n.name)
              if (idx > -1) newSelectedLeafNames.splice(idx, 1)
            }
            n.children?.forEach(removeDescendants)
          }
          removeDescendants(node)
        }

        // Check if all leaves are selected
        const isAllSelected =
          newSelectedLeafIds.size === allLeafNodes.length &&
          allLeafNodes.length > 0

        const newSelection: HierarchySelection = {
          selectedIds: newSelectedIds,
          selectedLeafIds: newSelectedLeafIds,
          selectedLeafNames: newSelectedLeafNames,
          branchType: isAllSelected ? 'all' : 'regular',
          predefinedSelection: isAllSelected ? 'all' : null,
        }

        onChange?.(convertToSelection(newSelection))
        return newSelection
      })
    },
    [allLeafNodes, onChange, convertToSelection]
  )

  // Handle node expansion
  const handleNodeExpand = useCallback(
    (node: HierarchyNode, levelIndex: number) => {
      setExpandedPath((prev) => {
        // Truncate path to current level and add new node
        const newPath = prev.slice(0, levelIndex)
        newPath.push(node.id)
        return newPath
      })
    },
    []
  )

  // Handle predefined selection (All/Same/New Stores)
  const handlePredefinedSelect = useCallback(
    (type: 'all' | 'same' | 'new', checked: boolean) => {
      if (checked) {
        const newSelection = createPredefinedHierarchySelection(
          type,
          hierarchyData,
          tenant
        )
        setSelection(newSelection)
        onChange?.(convertToSelection(newSelection))
      } else {
        setSelection(emptyHierarchySelection)
        onChange?.(convertToSelection(emptyHierarchySelection))
      }
    },
    [hierarchyData, tenant, onChange, convertToSelection]
  )

  // Remove a selected chip
  const handleRemoveChip = useCallback(
    (leafId: string, leafName: string) => {
      setSelection((prev) => {
        // Ensure all IDs are strings when copying Sets
        const newSelectedIds = new Set<string>(
          Array.from(prev.selectedIds).filter(
            (id): id is string => typeof id === 'string'
          )
        )
        const newSelectedLeafIds = new Set<string>(
          Array.from(prev.selectedLeafIds).filter(
            (id): id is string => typeof id === 'string'
          )
        )
        const newSelectedLeafNames = prev.selectedLeafNames.filter(
          (name) => name !== leafName
        )

        newSelectedIds.delete(leafId)
        newSelectedLeafIds.delete(leafId)

        const newSelection: HierarchySelection = {
          selectedIds: newSelectedIds,
          selectedLeafIds: newSelectedLeafIds,
          selectedLeafNames: newSelectedLeafNames,
          branchType: 'regular',
          predefinedSelection: null,
        }

        onChange?.(convertToSelection(newSelection))
        return newSelection
      })
    },
    [onChange, convertToSelection]
  )

  // Handle search result selection
  const handleSearchResultSelect = useCallback(
    (node: HierarchyNode, checked: boolean) => {
      handleNodeSelect(node, checked)
      setSearchQuery('')
    },
    [handleNodeSelect]
  )

  // Get display text for trigger button
  const getDisplayText = useCallback(() => {
    if (selection.predefinedSelection === 'all')
      return t('branchSelect.allStores')
    if (selection.predefinedSelection === 'same')
      return t('branchSelect.sameStores')
    if (selection.predefinedSelection === 'new')
      return t('branchSelect.newStores')

    const count = selection.selectedLeafIds.size
    if (count === 0) return t('branchSelect.selectBranches')
    if (count <= 3) return selection.selectedLeafNames.join(', ')
    return `${count} ${t('branchSelect.storesSelected')}`
  }, [selection, t])

  // Loading state
  if (regionsLoading || REGIONS.length === 0) {
    return (
      <Button
        variant="outline"
        className="justify-start h-8 font-normal text-black w-[300px]"
        disabled
      >
        <span className="truncate">{t('branchSelect.loading')}</span>
        <IconChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
      </Button>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="justify-start h-8 font-normal w-[300px]"
              >
                <span className="truncate">{getDisplayText()}</span>
                <IconChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-[300px] whitespace-pre-line"
          >
            {selection.selectedLeafNames.join('\n') ||
              t('branchSelect.noSelection')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-auto max-w-[1000px] p-0" align="start">
        <div className="flex flex-col h-[350px]">
          {/* Content Area - Side by Side Layout */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Side: Badges and Predefined Options */}
            <div className="w-[230px] border-r border-gray-200 flex flex-col flex-shrink-0">
              <div className="flex items-center gap-2 p-3 border-b border-gray-200 flex-shrink-0">
                {/* Search Input */}
                <div className="relative flex-1">
                  <IconSearch className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('branchSelect.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8"
                  />

                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-gray-200 rounded-md shadow-lg z-50 max-h-[200px] overflow-auto">
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() =>
                            handleSearchResultSelect(
                              result,
                              !selection.selectedLeafIds.has(String(result.id))
                            )
                          }
                        >
                          <Checkbox
                            checked={selection.selectedLeafIds.has(
                              String(result.id)
                            )}
                            onCheckedChange={(checked) =>
                              handleSearchResultSelect(
                                result,
                                checked as boolean
                              )
                            }
                          />
                          <span className="text-sm">{result.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 flex-1">
                {/* Predefined Options */}
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">
                    {t('branchSelect.predefinedOptions') ||
                      'Predefined Options'}
                  </div>
                  <div className="space-y-1">
                    <TreeNode
                      icon={null}
                      label={t('branchSelect.allStores')}
                      checked={selection.predefinedSelection === 'all'}
                      onCheck={(c) => handlePredefinedSelect('all', c)}
                      onClick={() => {}}
                      className="text-sm"
                      type="date"
                    />
                    <TreeNode
                      icon={null}
                      label={t('branchSelect.sameStores')}
                      checked={selection.predefinedSelection === 'same'}
                      onCheck={(c) => handlePredefinedSelect('same', c)}
                      onClick={() => {}}
                      className="text-sm"
                      type="date"
                      disabled={tenant !== 'dev'}
                    />
                    <TreeNode
                      icon={null}
                      label={t('branchSelect.newStores')}
                      checked={selection.predefinedSelection === 'new'}
                      onCheck={(c) => handlePredefinedSelect('new', c)}
                      onClick={() => {}}
                      className="text-sm"
                      type="date"
                      disabled={tenant !== 'dev'}
                    />
                  </div>
                </div>

                {/* Selected Chips */}
                <div className="mb-2 mt-2">
                  <div className="max-h-[130px] overflow-y-auto">
                    <div className="text-xs font-medium text-gray-500 mb-2">
                      {t('branchSelect.selectedStores') || 'Selected Stores'}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Array.from(selection.selectedLeafIds)
                        .slice(0, 10)
                        .map((leafId) => {
                          const leafName =
                            allLeafNodes.find((l) => l.id === leafId)?.name ||
                            leafId
                          return (
                            <Badge
                              key={leafId}
                              variant="primaryScaler"
                              className="flex-shrink-0 gap-1 pr-1"
                            >
                              <span className="truncate max-w-[80px]">
                                {leafName}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveChip(String(leafId), leafName)
                                }}
                                className="hover:bg-gray-300 rounded-full p-0.5"
                              >
                                <IconX className="h-3 w-3" />
                              </button>
                            </Badge>
                          )
                        })}
                      {selection.selectedLeafIds.size === 0 && (
                        <span className="text-xs text-gray-400">
                          {t('branchSelect.noSelection')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Multi-Column Selection Area */}
            <div className="flex-1 overflow-x-auto overflow-y-auto picker-content [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="flex h-full">
                {columnsToRender.map((column, index) => (
                  <LevelColumn
                    key={`${column.title}-${index}`}
                    title={column.title}
                    nodes={column.nodes}
                    selectedIds={selection.selectedIds}
                    expandedId={expandedPath[index] || null}
                    onSelect={handleNodeSelect}
                    onExpand={(node) => handleNodeExpand(node, index)}
                    isLeafLevel={column.nodes.every((n) => isLeafNode(n))}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default BranchSelect
